import Logo from 'components/common/Logo';
import { FC, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { CloseIcon } from '@medly-components/icons';
import { MAP_ROUTE } from 'constants/routes';
import { MapTourContext } from 'context/MapTourContext';
import { useSideNavContext } from 'context/SideNavContext/SideNavContext';
import { UserContext } from 'context/UserContext';
import { useIsMobile } from 'hooks';
import { useIsTab } from 'hooks/useIsTab';
import { Legends } from 'pages/Map/components/Legends/Legends';
import { TourType } from 'pages/Map/components/MapTour';
import MapSearchIcon from 'pages/MapSearch/MapSearchIcon';
import { useMatch } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useOutsideAlerter } from 'utils/utils';
import { ReactComponent as MenuIcon } from '../../../assets/svg/menu.svg';
import { FullScreenLoader } from '../../common/FullscreenLoader/FullscreenLoader';
import NavList from './NavList';
import { SideNavHoverContext } from './SideNav.context';
import * as Styled from './SideNav.styled';
import { CloseLegends, LegendHeader, LegendsPopoverWrapper, LegendsWrapper } from './SideNav.styled';
import SidenavHeader from './SidenavHeader';
import ToggleSwitch from './ToggleSwitch';
import { useSideNavLinks } from './useSideNavLinks';

export const SideNav: FC = memo(() => {
    const ref = useRef(null);
    const legendsWrapperRef = useRef<HTMLInputElement>(null);
    const { user } = useContext(UserContext);
    const { isCurrentTourItem } = useContext(MapTourContext);
    const theme = useTheme();
    const isTab = useIsTab();
    const isMobile = useIsMobile();
    const isMapRoute = useMatch(MAP_ROUTE);

    // Maintaining this hover state in this component only so that it does not re render the whole app
    const [isHovered, setHoveredState] = useState(false);
    const { isExpanded, setExpandedState, showMobileMenu } = useSideNavContext();

    const openSidenav = () => {
        !isMobile && setHoveredState(true);
    };
    const closeSidenav = () => setHoveredState(false);
    const expandSidenav = () => setExpandedState(true);
    const collapseSidenav = () => {
        setExpandedState(false);
        setHoveredState(false);
    };
    const [showLegends, setShowLegends] = useState<boolean>(false);

    useEffect(() => {
        if (isCurrentTourItem(TourType.INFO_ABOUT_LEGENEDS)) {
            expandSidenav();
        }
    }, [isCurrentTourItem]);

    const toggleLegends = useCallback(() => {
        setShowLegends(!showLegends);
        if (window.innerWidth <= theme.breakpoints.M.min) {
            collapseSidenav();
        }
    }, [setShowLegends, collapseSidenav]);

    const setLegendsClose = useCallback(() => {
        setShowLegends(false);
    }, [setShowLegends]);

    const resizeWindow = useCallback(() => {
        if (showLegends) {
            if (window.innerWidth <= theme.breakpoints.M.min) {
                isExpanded && collapseSidenav();
            } else {
                !isExpanded && expandSidenav();
            }
        }
    }, [showLegends, collapseSidenav, expandSidenav]);
    const admin = !!user?.isAdmin;
    const [links, loading] = useSideNavLinks(showLegends, admin, toggleLegends);
    useOutsideAlerter(legendsWrapperRef, setLegendsClose, { exceptionEleId: 'Legends-nav-item' });

    useEffect(() => {
        window.addEventListener('resize', resizeWindow);
    }, [resizeWindow, showLegends]);

    return (
        <SideNavHoverContext.Provider value={isHovered}>
            <FullScreenLoader loading={loading} />
            {isExpanded ? <Styled.MobileBackdrop onClick={collapseSidenav} /> : ''}
            {showMobileMenu ? (
                <Styled.MobileNavHeader>
                    <MenuIcon onClick={isExpanded ? collapseSidenav : expandSidenav} />
                    <Styled.LogoWrapper>
                        <Logo />
                    </Styled.LogoWrapper>
                    {isTab && isMapRoute ? <MapSearchIcon /> : null}
                </Styled.MobileNavHeader>
            ) : null}
            <Styled.Aside ref={ref} position="left" isExpanded={isExpanded} isHovered={isHovered}>
                <Styled.Nav isHovered={isHovered} isExpanded={isExpanded} onMouseEnter={openSidenav} onMouseLeave={closeSidenav}>
                    <SidenavHeader />
                    <Styled.CloseNav onClick={collapseSidenav}>
                        <CloseIcon size="S" iconColor={theme.colors.silverGrey[500]} />
                    </Styled.CloseNav>
                    <NavList links={links} expandSidenav={expandSidenav} />
                    <Styled.ToggleSwitchStyled>
                        <ToggleSwitch isExpanded={isExpanded} onClick={isExpanded ? collapseSidenav : expandSidenav} />
                    </Styled.ToggleSwitchStyled>
                </Styled.Nav>
            </Styled.Aside>
            {showLegends ? (
                <LegendsPopoverWrapper ref={legendsWrapperRef} id="legends-popover">
                    <LegendHeader textVariant="h2">Legends</LegendHeader>
                    <LegendsWrapper>
                        <CloseLegends onClick={setLegendsClose}>
                            <CloseIcon size="S" iconColor={theme.colors.darkBlue} />
                        </CloseLegends>
                        <Legends altName iconSize="2.4rem" display="block" padding="0.8rem 0" textPadding="0 0 0 0.8rem" />
                    </LegendsWrapper>
                </LegendsPopoverWrapper>
            ) : null}
        </SideNavHoverContext.Provider>
    );
});

SideNav.displayName = 'SideNav';
